import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

export const SITE_DOMAIN = 'https://www.vsrinsuranceplus.com';
export const DEFAULT_OG_IMAGE = `${SITE_DOMAIN}/og-image.png`;
export const ADVISOR_NAME = 'V. S. Reddy';
export const ADVISOR_PHONE = '+919849664800';
export const ADVISOR_PHONE_DISPLAY = '+91-9849664800';
export const ADVISOR_EMAIL = 'vsrinsuranceplus@gmail.com';
export const ADVISOR_LOCATION = 'Hyderabad, Telangana, India';

const SEOHead = ({
  title,
  description,
  canonicalPath = '',
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  schemaJson,
  noIndex = false,
  publishedTime,
  modifiedTime,
  author = ADVISOR_NAME,
  breadcrumbs = []
}) => {
  const { i18n } = useTranslation();
  const currentLang = i18n?.language === 'te' ? 'te' : 'en';

  const fullCanonicalUrl = `${SITE_DOMAIN}${canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`}`;
  const cleanCanonicalUrl = fullCanonicalUrl.endsWith('/') && fullCanonicalUrl !== `${SITE_DOMAIN}/` 
    ? fullCanonicalUrl.slice(0, -1) 
    : fullCanonicalUrl;

  const pageTitle = title.includes('VSR Insurance Plus') || title.includes('V. S. Reddy') 
    ? title 
    : `${title} | VSR Insurance Plus - V. S. Reddy`;

  // Base Organization & LocalBusiness schema
  const businessSchema = {
    '@context': 'https://schema.org',
    '@type': 'InsuranceAgency',
    '@id': `${SITE_DOMAIN}/#organization`,
    'name': 'VSR Insurance Plus',
    'alternateName': 'SVR LIC Services',
    'url': SITE_DOMAIN,
    'logo': `${SITE_DOMAIN}/logo.png`,
    'image': DEFAULT_OG_IMAGE,
    'description': 'Trusted Insurance Advisory led by V. S. Reddy with 25+ years of experience in LIC life insurance, child plans, retirement pensions, and HDFC ERGO health insurance.',
    'telephone': ADVISOR_PHONE,
    'email': ADVISOR_EMAIL,
    'address': {
      '@type': 'PostalAddress',
      'streetAddress': 'SVR LIC Services, Dilsukhnagar / Hyderabad Central',
      'addressLocality': 'Hyderabad',
      'addressRegion': 'Telangana',
      'postalCode': '500060',
      'addressCountry': 'IN'
    },
    'geo': {
      '@type': 'GeoCoordinates',
      'latitude': '17.3850',
      'longitude': '78.4867'
    },
    'openingHoursSpecification': [
      {
        '@type': 'OpeningHoursSpecification',
        'dayOfWeek': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        'opens': '09:00',
        'closes': '20:00'
      }
    ],
    'priceRange': '₹₹',
    'founder': {
      '@type': 'Person',
      '@id': `${SITE_DOMAIN}/#advisor`,
      'name': ADVISOR_NAME,
      'jobTitle': 'Senior Insurance Advisor & Financial Consultant',
      'description': 'Senior Life Insurance Advisor with LIC of India since 2000 with 25+ years of verified industry expertise guiding 10,000+ families across India and worldwide NRIs.',
      'sameAs': [
        'https://www.linkedin.com/in/veereddy-srinivas-reddy-23914a5a/',
        'https://www.instagram.com/vsr_insurance_plus'
      ]
    },
    'areaServed': [
      {
        '@type': 'Country',
        'name': 'India'
      },
      {
        '@type': 'AdministrativeArea',
        'name': 'Telangana'
      },
      {
        '@type': 'AdministrativeArea',
        'name': 'Andhra Pradesh'
      },
      {
        '@type': 'Place',
        'name': 'Global NRIs (USA, UAE, UK, Singapore, Canada, Gulf)'
      }
    ],
    'sameAs': [
      'https://www.linkedin.com/in/veereddy-srinivas-reddy-23914a5a/',
      'https://www.instagram.com/vsr_insurance_plus'
    ]
  };

  // Breadcrumb schema if provided
  let breadcrumbSchema = null;
  if (breadcrumbs && breadcrumbs.length > 0) {
    breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': [
        {
          '@type': 'ListItem',
          'position': 1,
          'name': 'Home',
          'item': SITE_DOMAIN
        },
        ...breadcrumbs.map((crumb, idx) => ({
          '@type': 'ListItem',
          'position': idx + 2,
          'name': crumb.name,
          'item': `${SITE_DOMAIN}${crumb.path.startsWith('/') ? crumb.path : `/${crumb.path}`}`
        }))
      ]
    };
  }

  return (
    <Helmet>
      {/* Basic Metadata */}
      <html lang={currentLang} />
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={cleanCanonicalUrl} />

      {/* Crawl Control */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Verification Tags (from environment if present) */}
      {process.env.REACT_APP_GSC_VERIFICATION && (
        <meta name="google-site-verification" content={process.env.REACT_APP_GSC_VERIFICATION} />
      )}
      {process.env.REACT_APP_BING_VERIFICATION && (
        <meta name="msvalidate.01" content={process.env.REACT_APP_BING_VERIFICATION} />
      )}

      {/* Hreflang alternates */}
      <link rel="alternate" hrefLang="en-IN" href={cleanCanonicalUrl} />
      <link rel="alternate" hrefLang="te-IN" href={`${cleanCanonicalUrl}?lang=te`} />
      <link rel="alternate" hrefLang="x-default" href={cleanCanonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:site_name" content="VSR Insurance Plus" />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={cleanCanonicalUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={pageTitle} />
      <meta property="og:locale" content={currentLang === 'te' ? 'te_IN' : 'en_IN'} />

      {/* Article specific OG tags */}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {author && <meta property="article:author" content={author} />}

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={cleanCanonicalUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:creator" content="@vsrinsuranceplus" />

      {/* Primary Organization Schema on all pages */}
      <script type="application/ld+json">
        {JSON.stringify(businessSchema)}
      </script>

      {/* Breadcrumb Schema */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}

      {/* Custom Page Schema (Article, Service, FAQ, etc.) */}
      {schemaJson && (
        <script type="application/ld+json">
          {JSON.stringify(schemaJson)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
